import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getReportClassList } from "hooks/useFilterPersistence/useFilterPersistence";
import styled from "styled-components";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSort,
  faSortUp,
  faSortDown,
  faTimes,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { colors } from "utils";
import { request } from "hooks/useOkoApi/useOkoApi";
import { saveFilters, loadFilters, loadSharedFilters, resolveClassId } from "utils/persistFilters";
import { ProficiencyLevelBadge } from "./ProficiencyLevelBadge";
import { ProficiencyCell, STATUS_CONFIG } from "./ProficiencyCell";
import { RubricLink } from "components/pages/Educator/StudentReports/RubricLink";
import {
  bodyCellStyles,
  proficiencyCsvCell,
  compareByProficiency,
  sessionHistoryUrl,
  STUDENT_NAME_COLOR,
} from "./proficiencyTable";
import { FeedbackCTA } from "components/shared/FeedbackCTA/FeedbackCTA";

import { SelectDropdown, ClearAllButton } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { pageTitleInset } from "components/pageTitleInset";

// --- Level config --- one palette for the cards, the badges and the cells.
const LEVEL_CONFIG = {
  1: { ...STATUS_CONFIG.LEVEL_1, name: "Needs Support" },
  2: { ...STATUS_CONFIG.LEVEL_2, name: "Needs Practice" },
  3: { ...STATUS_CONFIG.LEVEL_3, name: "Ready to Lead" },
};

// --- CSV Export ---
const exportCsv = (proficiencyData) => {
  if (!proficiencyData) return;
  const { standards, students } = proficiencyData;
  const headers = ["Student", ...standards.map((s) => s.standard_code)];
  const rows = students.map((student) => {
    const cells = standards.map((s) => {
      const entry = student.standards[s.standard_code];
      return proficiencyCsvCell(entry);
    });
    return [student.student_name, ...cells];
  });
  const csvContent = [headers, ...rows]
    .map((row) => row.map((cell) => `"${cell}"`).join(","))
    .join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `proficiency_${proficiencyData.classroom_name}_${proficiencyData.grade}_${proficiencyData.domain_code}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const Proficiency = ({ educatorId, sharedClassId, onClassChange, embedded = false }) => {
  // Filter state
  const queryClient = useQueryClient();
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassIdInternal] = useState(null);

  const setSelectedClassId = (id) => {
    setSelectedClassIdInternal(id);
    if (onClassChange) onClassChange(id);
  };
  const [filters, setFilters] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  // Starts true so the table does not fetch before the first filter init runs.
  const [filtersLoading, setFiltersLoading] = useState(true);

  // Data state — cached per (class, grade, domain): a revisited combination
  // paints instantly while revalidating in the background; the spinner only
  // covers an uncached one (OKO2-3684).
  const {
    data: proficiencyData = null,
    isInitialLoading: loading,
  } = useQuery({
    queryKey: [
      "proficiency",
      selectedClassId,
      selectedGrade,
      selectedDomain?.code ?? null,
      educatorId ?? null,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (selectedGrade) params.set("grade", selectedGrade);
      if (selectedDomain) params.set("domain_code", selectedDomain.code);
      if (educatorId) params.set("educator_id", educatorId);
      const qs = params.toString();
      return request(`class-report/${selectedClassId}/proficiency${qs ? `?${qs}` : ""}`, { method: "GET" });
    },
    // No grade means every grade. Held while the filters resolve so a class
    // change or a grade change does not fetch twice.
    enabled: Boolean(selectedClassId) && !filtersLoading,
    staleTime: 0,
  });
  // Sort state
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // Track whether initial load is done so we only persist user-driven changes
  const [filtersReady, setFiltersReady] = useState(false);

  // Saved grade/domain hint set during initial load; consumed on first init to
  // rehydrate user's prior selections (validated against the class's filters).
  const savedFiltersHintRef = useRef(null);

  // Persist filters to localStorage for cross-tab sharing (only after init)
  useEffect(() => {
    if (selectedClassId && filtersReady) {
      // Broadcast the domain as the shared standard (null when cleared) so
      // pages that filter by standard stay in sync — their dropdowns accept
      // domain codes the same as specific standard codes.
      saveFilters("proficiency", {
        selectedClassId,
        selectedGrade,
        selectedDomain,
        selectedStandard: selectedDomain?.code || null,
        standardDisplayLabel: selectedDomain
          ? `${selectedDomain.code}: ${selectedDomain.name}`
          : null,
      });
    }
  }, [selectedClassId, selectedGrade, selectedDomain, filtersReady]);

  // Fetch classes on mount
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const classList = await getReportClassList(queryClient, educatorId);
        setClasses(classList);
        if (classList.length > 0) {
          let resolvedClassId;
          let savedProf;
          if (sharedClassId && classList.some(c => c.id === sharedClassId)) {
            resolvedClassId = sharedClassId;
            savedProf = loadFilters("proficiency");
          } else {
            const resolved = await resolveClassId("proficiency", classList);
            resolvedClassId = resolved.classId;
            savedProf = resolved.saved;
          }
          // Shared filter (set by any page) is authoritative for the current
          // class; only fall back to the page-specific saved domain when shared
          // is absent or refers to a different class.
          const shared = loadSharedFilters();
          const sharedMatchesClass =
            shared?.selectedClassId === resolvedClassId;
          const savedMatchesClass =
            savedProf?.selectedClassId === resolvedClassId;
          const hint = {};
          if (savedMatchesClass && savedProf.selectedGrade) {
            hint.grade = savedProf.selectedGrade;
          }
          if (sharedMatchesClass) {
            // May be a specific standard (e.g. "4.G.1") or a domain code
            // (e.g. "4.G"); init will validate against available domains.
            hint.sharedStandard = shared.selectedStandard ?? null;
          } else if (savedMatchesClass && savedProf.selectedDomain) {
            hint.domain = savedProf.selectedDomain;
          }
          if (Object.keys(hint).length > 0) {
            savedFiltersHintRef.current = hint;
          }
          setSelectedClassId(resolvedClassId);
        }
      } catch (err) {
        console.error("Failed to fetch classes:", err);
      }
    };
    fetchClasses();
  }, [educatorId, queryClient]);

  // Normalize domain objects — API may use "code"/"domain_code"
  const normalizeDomains = (domains) =>
    (domains || []).map((d) => ({
      code: d.code || d.domain_code,
      name: d.name || d.domain_name,
    }));

  // Fetch filters with current selections so the other dropdown narrows
  const fetchFilters = useCallback(async (classId, grade, domainCode) => {
    if (!classId) return null;
    const params = new URLSearchParams();
    if (grade) params.append("grade", grade);
    if (domainCode) params.append("domain_code", domainCode);
    const qs = params.toString();
    const data = await request(
      `class-report/${classId}/proficiency/filters${qs ? `?${qs}` : ""}`,
      { method: "GET" }
    );
    return {
      ...data,
      domains: normalizeDomains(data.domains),
    };
  }, []);

  // On class change — fetch unfiltered to pick defaults, then re-fetch with defaults to narrow
  useEffect(() => {
    if (!selectedClassId) return;
    let cancelled = false;
    // One-shot: rehydrate saved grade/domain on the first init after a page load.
    const hint = savedFiltersHintRef.current;
    savedFiltersHintRef.current = null;
    const init = async () => {
      setFiltersLoading(true);
      setFilters(null);
      // Clearing the grade disables the proficiency query, so the table data
      // follows the filters without an explicit reset here.
      setSelectedGrade(null);
      setSelectedDomain(null);
      try {
        // 1. Fetch all available filters for this class
        const allFilters = await fetchFilters(selectedClassId, null, null);
        if (cancelled) return;
        const desiredGrade = hint?.grade ?? null;
        const savedGradeValid =
          desiredGrade && allFilters.grades?.some((g) => g.grade === desiredGrade);
        // All Grades unless a saved grade still applies to this class.
        const gradeToUse = savedGradeValid ? desiredGrade : null;
        // 2. Fetch filters narrowed by grade to get valid domains for that grade
        if (gradeToUse) {
          const gradeFilters = await fetchFilters(selectedClassId, gradeToUse, null);
          if (cancelled) return;
          // Resolve the domain hint. When the hint is a raw standard code from
          // the shared filter (hint.sharedStandard), map it to the class's
          // domains — accept an exact domain-code match or the domain prefix
          // of a specific standard (e.g. "4.G.1" → "4.G"). Absent/null means
          // the shared filter was cleared, so no domain should be selected.
          let desiredDomain = hint?.domain ?? null;
          if (!desiredDomain && hint && "sharedStandard" in hint) {
            const code = hint.sharedStandard;
            if (code && gradeFilters.domains?.length) {
              desiredDomain =
                gradeFilters.domains.find((d) => d.code === code) ||
                gradeFilters.domains.find((d) => code.startsWith(`${d.code}.`)) ||
                null;
            }
          }
          const savedDomainValid =
            desiredDomain &&
            gradeFilters.domains?.some((d) => d.code === desiredDomain.code);
          const domainToUse = savedDomainValid
            ? gradeFilters.domains.find((d) => d.code === desiredDomain.code)
            : null;
          // Keep all grades from unfiltered fetch, use domains from grade-filtered fetch
          setFilters({ ...gradeFilters, grades: allFilters.grades });
          setSelectedGrade(gradeToUse);
          setSelectedDomain(domainToUse);
        } else {
          let desiredDomain = hint?.domain ?? null;
          if (!desiredDomain && hint && "sharedStandard" in hint) {
            const code = hint.sharedStandard;
            if (code && allFilters.domains?.length) {
              desiredDomain =
                allFilters.domains.find((d) => d.code === code) ||
                allFilters.domains.find((d) => code.startsWith(`${d.code}.`)) ||
                null;
            }
          }
          setFilters(allFilters);
          setSelectedDomain(
            desiredDomain && allFilters.domains?.some((d) => d.code === desiredDomain.code)
              ? allFilters.domains.find((d) => d.code === desiredDomain.code)
              : null
          );
        }
      } catch (err) {
        console.error("Failed to fetch proficiency filters:", err);
      } finally {
        if (!cancelled) {
          setFiltersLoading(false);
          setFiltersReady(true);
        }
      }
    };
    init();
    return () => { cancelled = true; };
  }, [selectedClassId, fetchFilters]);

  // On grade change — re-fetch filters to get valid domains for the new grade
  const handleGradeChange = async (grade) => {
    setSelectedGrade(grade);
    setFiltersLoading(true);
    try {
      const gradeFilters = await fetchFilters(selectedClassId, grade, null);
      const domainStillValid = selectedDomain && gradeFilters.domains?.some((d) => d.code === selectedDomain.code);
      if (!domainStillValid) setSelectedDomain(null);
      // Keep existing grades list (all grades for this class), update domains for new grade
      setFilters((prev) => ({ ...gradeFilters, grades: prev?.grades || gradeFilters.grades }));
    } catch (err) {
      console.error("Failed to fetch filters after grade change:", err);
    } finally {
      setFiltersLoading(false);
    }
  };

  // On domain change — domain does not affect available grades
  const handleDomainChange = (domain) => {
    setSelectedDomain(domain);
  };

  const hasFilters = Boolean(selectedGrade || selectedDomain);
  const handleClearAll = () => {
    setSelectedDomain(null);
    handleGradeChange(null);
  };

  // Client-side sorting
  const sortedStudents = useMemo(() => {
    if (!proficiencyData?.students) return [];
    if (!sortBy) return proficiencyData.students;

    return [...proficiencyData.students].sort((a, b) => {
      if (sortBy !== "student_name") {
        return compareByProficiency(a, b, sortBy, sortOrder);
      }
      const valA = a.student_name.toLowerCase();
      const valB = b.student_name.toLowerCase();
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [proficiencyData, sortBy, sortOrder]);

  // Sort handler
  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortOrder("asc");
    }
  };

  // Cell click handler — open session history in new tab with filters pre-filled
  const handleCellClick = ({ studentId, standardCode }) => {
    const url = sessionHistoryUrl({
      classroomId: selectedClassId,
      studentId,
      standardCode,
    });
    window.open(url, "_blank");
  };

  const selectedClassName =
    classDisplayName(classes.find((c) => c.id === selectedClassId)) || "";

  return (
    <Container $embedded={embedded}>
      {!embedded && (
        <Header>
          <HeaderTitle>Proficiency</HeaderTitle>
        </Header>
      )}

      <FilterSection>
        <FilterCard>
          <FilterDropdowns>
            {!embedded && (
              <SelectDropdown
                label="Class"
                value={selectedClassId}
                displayText={selectedClassName || "Select class"}
                options={classes.map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
                groupBySchoolYear
                onChange={(val) => setSelectedClassId(val)}
              />
            )}

            {/* Grade Dropdown */}
            <GradeSlot>
            <SelectDropdown
              label="Grade"
              value={selectedGrade}
              displayText={
                filtersLoading
                  ? "Loading..."
                  : selectedGrade
                    ? `${selectedGrade}${getGradeSuffix(selectedGrade)} Grade`
                    : "All Grades"
              }
              options={[
                { value: null, label: "All Grades" },
                ...(filters?.grades || []).map((g) => ({
                  value: g.grade,
                  label: `${g.grade}${getGradeSuffix(g.grade)} Grade`,
                })),
              ]}
              onChange={(val) => handleGradeChange(val)}
            />
            </GradeSlot>

            {/* Domain/Standard Searchable Dropdown */}
            <DomainSearchDropdown
              domains={filters?.domains || []}
              selectedDomain={selectedDomain}
              onSelect={handleDomainChange}
              onClear={() => handleDomainChange(null)}
              disabled={filtersLoading || !filters?.domains?.length}
              loading={filtersLoading}
            />
            {hasFilters && <ClearAllButton onClick={handleClearAll}>Clear All</ClearAllButton>}
          </FilterDropdowns>

          <ExportButton onClick={() => exportCsv(proficiencyData)} disabled={!proficiencyData}>
            Export .csv
          </ExportButton>
        </FilterCard>
      </FilterSection>

      <Body>
        {/* Description */}
        <DescriptionSection>
          <DescriptionText>
            The Math Proficiency levels reflect student accuracy on the 5 most
            recent Solo Time questions in each standard (WIP means fewer than 5
            questions answered). Use the levels to determine who needs
            reteaching, who needs more practice, and who is ready to extend or
            lead a discussion.{" "}
            <RubricLink
              href="https://docs.google.com/document/d/1Ft503fy1Ui5ZDbZSDu8IsxxXqmVSSkjtrf_Ft4znzt4/edit?tab=t.0"
              variant="inline"
            >
              View the rubric details
            </RubricLink>
          </DescriptionText>
        </DescriptionSection>

        {/* Summary Cards — always visible */}
        <SummaryCards>
          {[1, 2, 3].map((lvl) => {
            const config = LEVEL_CONFIG[lvl];
            return (
              <SummaryCard key={lvl}>
                <ProficiencyLevelBadge level={lvl} color={config.color} size={48} fontSize={18} />
                <SummaryCardFooter>
                  <LevelTag $bg={config.bg} $color={config.color} $border={config.border}>
                    {config.label}
                  </LevelTag>
                  <LevelName>{config.name}</LevelName>
                </SummaryCardFooter>
              </SummaryCard>
            );
          })}
        </SummaryCards>

        {/* Table Skeleton */}
        {loading && (
          <TableContainer>
            <Table>
              <thead>
                <TableHeaderRow>
                  <SkeletonHeaderCell style={{ width: 159 }}>
                    <SkeletonBar $w="60px" />
                  </SkeletonHeaderCell>
                  {[1, 2, 3, 4].map((i) => (
                    <SkeletonHeaderCell key={i} style={{ width: 1 }}>
                      <SkeletonBar $w="70px" />
                    </SkeletonHeaderCell>
                  ))}
                  <SpacerHeaderCell aria-hidden="true" />
                </TableHeaderRow>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <TableBodyRow key={row}>
                    <SkeletonBodyCell style={{ width: 159 }}>
                      <SkeletonBar $w="80px" />
                    </SkeletonBodyCell>
                    {[1, 2, 3, 4].map((col) => (
                      <SkeletonBodyCell key={col} style={{ width: 1, whiteSpace: "nowrap" }}>
                        <SkeletonBar $w="60px" />
                      </SkeletonBodyCell>
                    ))}
                    <SpacerBodyCell aria-hidden="true" />
                  </TableBodyRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {/* Table */}
        {!loading && proficiencyData && proficiencyData.standards?.length > 0 && (
          <TableContainer>
            <Table>
              <thead>
                <TableHeaderRow>
                  <StudentHeaderCell onClick={() => handleSort("student_name")}>
                    <span>Student</span>
                    <SortIcon
                      column="student_name"
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                    />
                  </StudentHeaderCell>
                  {proficiencyData.standards.map((standard) => (
                    <StandardHeaderCell key={standard.standard_code}>
                      <HeaderCellContent>
                        <HeaderTitleGroup>
                          <span>{standard.standard_code.split(":")[0].trim()}</span>
                          <OverlayTrigger
                            placement="top"
                            trigger={["hover", "focus"]}
                            overlay={
                              <StyledTooltip id={`tooltip-${standard.standard_code}`}>
                                <TooltipTitle>{standard.standard_code.split(":")[0].trim()}</TooltipTitle>
                                <TooltipDesc>{standard.standard_name || standard.standard_code.split(":").slice(1).join(":").trim()}</TooltipDesc>
                              </StyledTooltip>
                            }
                          >
                            <InfoIcon type="button" aria-label={`Standard details for ${standard.standard_code.split(":")[0].trim()}`}>
                              <InfoSvg />
                            </InfoIcon>
                          </OverlayTrigger>
                        </HeaderTitleGroup>
                        <SortButton
                          onClick={() => handleSort(standard.standard_code)}
                          aria-label={`Sort by ${standard.standard_code}`}
                        >
                          <SortIcon
                            column={standard.standard_code}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                          />
                        </SortButton>
                      </HeaderCellContent>
                    </StandardHeaderCell>
                  ))}
                  <SpacerHeaderCell aria-hidden="true" />
                </TableHeaderRow>
              </thead>
              <tbody>
                {sortedStudents.map((student) => (
                  <TableBodyRow key={student.student_id}>
                    <StudentBodyCell>{student.student_name}</StudentBodyCell>
                    {proficiencyData.standards.map((standard) => (
                      <ProficiencyCell
                        key={standard.standard_code}
                        entry={student.standards[standard.standard_code]}
                        studentId={student.student_id}
                        studentName={student.student_name}
                        standardCode={standard.standard_code}
                        onCellClick={handleCellClick}
                      />
                    ))}
                    <SpacerBodyCell aria-hidden="true" />
                  </TableBodyRow>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}

        {/* Empty state */}
        {!loading && proficiencyData && proficiencyData.students?.length === 0 && (
          <EmptyState>No proficiency data available for the selected filters.</EmptyState>
        )}

        {!loading && !proficiencyData && selectedClassId && selectedGrade && selectedDomain && (
          <EmptyState>No data available. Try selecting different filters.</EmptyState>
        )}

        <FeedbackCTA reportLocation="proficiency" />
      </Body>
    </Container>
  );
};

// --- Sort Icon Component ---
const SortIcon = ({ column, sortBy, sortOrder }) => {
  if (sortBy === column) {
    return (
      <FontAwesomeIcon
        icon={sortOrder === "asc" ? faSortUp : faSortDown}
        style={{ opacity: 1 }}
      />
    );
  }
  return <FontAwesomeIcon icon={faSort} style={{ opacity: 0.5 }} />;
};

// --- Helpers ---
const getGradeSuffix = (grade) => {
  const num = parseInt(grade, 10);
  if (num === 1) return "st";
  if (num === 2) return "nd";
  if (num === 3) return "rd";
  return "th";
};

// --- Domain Search Dropdown ---
const DomainSearchDropdown = ({ domains, selectedDomain, onSelect, onClear, disabled, loading }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchText("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const filtered = searchText
    ? domains.filter((d) => {
      const text = `${d.code}: ${d.name}`.toLowerCase();
      return text.includes(searchText.toLowerCase());
    })
    : domains;

  const handleSelect = (domain) => {
    onSelect(domain);
    setIsOpen(false);
    setSearchText("");
  };

  return (
    <DomainDropdownContainer ref={containerRef}>
      <FilterLabel>Domain/Standard</FilterLabel>
      {isOpen ? (
        <DomainSelectBtn as="div" $focused>
          <DomainSearchInput
            ref={inputRef}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Type to search"
          />
          <DomainClearBtn onClick={() => { setIsOpen(false); setSearchText(""); }}>
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: 12, color: colors.milkGlass950 }} />
          </DomainClearBtn>
        </DomainSelectBtn>
      ) : (
        <DomainSelectBtn
          onClick={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          title={selectedDomain ? `${selectedDomain.code}: ${selectedDomain.name}` : undefined}
        >
          {selectedDomain ? (
            <>
              <DomainSelectText>
                {selectedDomain.code}: {selectedDomain.name}
              </DomainSelectText>
              <DomainClearBtn onClick={(e) => { e.stopPropagation(); onClear(); }}>
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: 12, color: colors.milkGlass950 }} />
              </DomainClearBtn>
            </>
          ) : (
            <>
              <DomainSelectText $placeholder>
                {loading ? "Loading..." : "Select domain"}
              </DomainSelectText>
              <FontAwesomeIcon icon={faSearch} style={{ fontSize: 12, color: colors.milkGlass950 }} />
            </>
          )}
        </DomainSelectBtn>
      )}
      {isOpen && (
        <DomainMenu>
          {filtered.length === 0 ? (
            <DomainNoResults>
              <DomainNoResultsTitle>No results found</DomainNoResultsTitle>
              <DomainNoResultsText>
                {searchText ? "Try a different keyword or check your spelling." : "No domains available for the selected filters."}
              </DomainNoResultsText>
            </DomainNoResults>
          ) : (
            <>
              <DomainGroupHeader>Domains</DomainGroupHeader>
              {filtered.map((d) => (
                <DomainMenuItem
                  key={d.code}
                  title={`${d.code}: ${d.name}`}
                  $active={selectedDomain?.code === d.code}
                  onClick={() => handleSelect(d)}
                >
                  {d.code}: {d.name}
                </DomainMenuItem>
              ))}
            </>
          )}
        </DomainMenu>
      )}
    </DomainDropdownContainer>
  );
};

// --- Styled Components ---
const Container = styled.div`
  background-color: ${colors.milkGlass200};
  min-height: ${(p) => (p.$embedded ? "0" : "100vh")};
`;

const Header = styled.div`
  background-color: ${colors.nightlyWoods500};
  padding: 24px 32px 24px ${pageTitleInset};
  height: 92px;
  display: flex;
  align-items: center;
`;

const HeaderTitle = styled.h1`
  font-family: "Plus Jakarta Sans", sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 44px;
  color: ${colors.milkGlass500};
  margin: 0;
`;

const FilterSection = styled.div`
  padding: 2rem 32px 1.5rem;
`;

const FilterCard = styled.div`
  background: transparent;
  display: flex;
  align-items: flex-end;
  gap: 1rem;
`;

const FilterDropdowns = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  flex: 1;
  min-width: 0;
`;

// The frame (4422:118780) sizes the filters rather than letting them share
// the row: Grade at 140px, Domain/Standard at 250px, Export at the far right.
const GradeSlot = styled.div`
  flex: 0 0 140px;
  min-width: 0;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  color: black;
  line-height: normal;
`;

const DomainDropdownContainer = styled.div`
  position: relative;
  flex: 0 0 250px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DomainSelectBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid ${(p) => (p.$focused ? colors.blue500 : colors.blue300)};
  border-radius: 6px;
  cursor: pointer;
  min-height: 40px;
  gap: 8px;

  &:hover {
    border-color: ${colors.blue400};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DomainSelectText = styled.span`
  font-size: 14px;
  color: ${(p) => (p.$placeholder ? colors.milkGlass950 : colors.nightlyWoods500)};
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  min-width: 0;
  flex: 1;
`;

const DomainSearchInput = styled.input`
  flex: 1;
  border: none;
  &:focus:not(:focus-visible) {
    outline: none;
  }
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: ${colors.milkGlass950};
  }
`;

const DomainClearBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DomainMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid ${colors.blue300};
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(14, 26, 49, 0.12);
  z-index: 100;
  max-height: 300px;
  overflow-y: auto;
  min-width: 320px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.milkGlass800};
    border-radius: 2px;
  }
  scrollbar-width: thin;
  scrollbar-color: ${colors.milkGlass800} transparent;
`;

const DomainGroupHeader = styled.div`
  padding: 8px 16px;
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #858B87;
  line-height: 18px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const DomainMenuItem = styled.div`
  padding: 8px 16px;
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  cursor: pointer;
  line-height: 24px;
  background: ${(p) => (p.$active ? colors.blue50 : "transparent")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: #F4F4FD;
  }
`;

const DomainNoResults = styled.div`
  padding: 8px 16px;
`;

const DomainNoResultsTitle = styled.div`
  font-family: "ABC Marfa", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #858B87;
  line-height: 18px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const DomainNoResultsText = styled.div`
  font-size: 14px;
  color: ${colors.nightlyWoods500};
  line-height: 20px;
`;

const ExportButton = styled.button`
  background: ${colors.blue500};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  height: 40px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const Body = styled.div`
  padding: 0 32px 64px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 0.5rem;
`;

const DescriptionText = styled.p`
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
  margin: 0;
`;

const SummaryCards = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0px 1px 3px rgba(14, 26, 49, 0.1),
    0px 1px 2px rgba(14, 26, 49, 0.06);
`;

const SummaryCard = styled.div`
  width: 180px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SummaryCardFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const LevelTag = styled.span`
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$border};
  border-radius: 11px;
  padding: 2px 8px;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
`;

const LevelName = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.nightlyWoods500};
`;


const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderRow = styled.tr`
  height: 54px;
  background: ${colors.nightlyWoods500};
`;

const headerCellStyles = `
  padding: 16px;
  color: white;
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  white-space: nowrap;
`;

const StudentHeaderCell = styled.th`
  ${headerCellStyles}
  width: 159px;
  min-width: 159px;
  cursor: pointer;
  user-select: none;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const StandardHeaderCell = styled.th`
  ${headerCellStyles}
  width: 1px;
`;

const SpacerHeaderCell = styled.th`
  background: ${colors.nightlyWoods500};
  width: auto;
  padding: 0;
`;

const SpacerBodyCell = styled.td`
  background: ${colors.gray50};
  width: auto;
  padding: 0;
`;

const HeaderCellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const HeaderTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoSvg = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M9 15H11V9H9V15ZM10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.59 18 2 14.41 2 10C2 5.59 5.59 2 10 2C14.41 2 18 5.59 18 10C18 14.41 14.41 18 10 18ZM9 7H11V5H9V7Z" fill="white" />
  </svg>
);

const InfoIcon = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  opacity: 0.8;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue500};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const SortButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
`;

const StyledTooltip = styled(Tooltip)`
  .tooltip-inner {
    background: #3d3d3d;
    border-radius: 11px;
    padding: 12px 24px;
    text-align: left;
    max-width: 280px;
    font-size: 16px;
  }
  .tooltip-arrow::before {
    border-top-color: #3d3d3d;
  }
`;

const TooltipTitle = styled.div`
  font-weight: 700;
  margin-bottom: 4px;
`;

const TooltipDesc = styled.div`
  font-weight: 400;
`;

const TableBodyRow = styled.tr`
  height: 62px;
  border-bottom: 1px solid #858b87;
`;


const StudentBodyCell = styled.td`
  ${bodyCellStyles}
  width: 159px;
  min-width: 159px;
  color: ${STUDENT_NAME_COLOR};
  background: ${colors.gray50};
  white-space: nowrap;
`;

const skeletonPulse = `
  @keyframes skeleton-pulse {
    0% { opacity: 0.6; }
    50% { opacity: 0.3; }
    100% { opacity: 0.6; }
  }
`;

const SkeletonHeaderCell = styled.th`
  ${headerCellStyles}
  background: ${colors.nightlyWoods500};
`;

const SkeletonBodyCell = styled.td`
  ${bodyCellStyles}
  background: ${colors.gray50};
`;

const SkeletonBar = styled.div`
  ${skeletonPulse}
  width: ${(p) => p.$w || "60px"};
  height: 14px;
  border-radius: 4px;
  background: ${colors.milkGlass600};
  animation: skeleton-pulse 1.5s ease-in-out infinite;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 0;
  font-size: 16px;
  color: ${colors.milkGlass950};
`;
